/**
 * Generate time-series forecasts using multiple methods and pick the best.
 */
export function forecast(skuId, periods, periodsAhead = 6) {
    const values = periods.map((p) => p.quantity);
    if (values.length < 3) {
        const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
        return {
            skuId,
            method: 'simple_ma',
            forecast: Array(periodsAhead).fill(Math.round(avg)),
            mae: 0, mape: 0, fitScore: Infinity,
        };
    }
    // Try all methods and pick the best
    const candidates = [
        simpleMovingAverage(skuId, values, periodsAhead, 3),
        simpleMovingAverage(skuId, values, periodsAhead, 6),
        weightedMovingAverage(skuId, values, periodsAhead),
        exponentialSmoothing(skuId, values, periodsAhead, 0.3),
        linearTrend(skuId, values, periodsAhead),
    ];
    return candidates.reduce((best, c) => c.fitScore < best.fitScore ? c : best);
}
function simpleMovingAverage(skuId, values, ahead, window) {
    const n = values.length;
    const usableWindow = Math.min(window, n);
    // In-sample fit
    const fitted = [];
    for (let i = usableWindow; i < n; i++) {
        let sum = 0;
        for (let j = i - usableWindow; j < i; j++)
            sum += values[j];
        fitted.push(sum / usableWindow);
    }
    const actual = values.slice(usableWindow);
    const mae = meanAbsError(actual, fitted);
    const mape = meanAbsPctError(actual, fitted);
    // Forecast
    const lastWindow = values.slice(-usableWindow);
    const avg = lastWindow.reduce((a, b) => a + b, 0) / usableWindow;
    const forecast = Array(ahead).fill(Math.round(avg));
    return { skuId, method: 'simple_ma', forecast, mae, mape, fitScore: mae };
}
function weightedMovingAverage(skuId, values, ahead) {
    const n = values.length;
    const window = Math.min(4, n);
    const weights = Array.from({ length: window }, (_, i) => i + 1);
    const weightSum = weights.reduce((a, b) => a + b, 0);
    const fitted = [];
    for (let i = window; i < n; i++) {
        let sum = 0;
        for (let j = 0; j < window; j++) {
            sum += values[i - window + j] * weights[j];
        }
        fitted.push(sum / weightSum);
    }
    const actual = values.slice(window);
    const mae = meanAbsError(actual, fitted);
    const mape = meanAbsPctError(actual, fitted);
    const lastValues = values.slice(-window);
    let forecastBase = 0;
    for (let j = 0; j < window; j++)
        forecastBase += lastValues[j] * weights[j];
    forecastBase /= weightSum;
    const forecast = Array(ahead).fill(Math.round(forecastBase));
    return { skuId, method: 'weighted_ma', forecast, mae, mape, fitScore: mae };
}
function exponentialSmoothing(skuId, values, ahead, alpha) {
    const n = values.length;
    const smoothed = [values[0]];
    for (let i = 1; i < n; i++) {
        smoothed.push(alpha * values[i] + (1 - alpha) * smoothed[i - 1]);
    }
    // In-sample: compare smoothed[i-1] (forecast) to actual[i]
    const fitted = smoothed.slice(0, -1);
    const actual = values.slice(1);
    const mae = meanAbsError(actual, fitted);
    const mape = meanAbsPctError(actual, fitted);
    const lastSmoothed = smoothed[smoothed.length - 1];
    const forecast = Array(ahead).fill(Math.round(lastSmoothed));
    return { skuId, method: 'exponential', forecast, mae, mape, fitScore: mae };
}
function linearTrend(skuId, values, ahead) {
    const n = values.length;
    const xMean = (n - 1) / 2;
    const yMean = values.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) {
        num += (i - xMean) * (values[i] - yMean);
        den += (i - xMean) ** 2;
    }
    const slope = den !== 0 ? num / den : 0;
    const intercept = yMean - slope * xMean;
    const fitted = values.map((_, i) => intercept + slope * i);
    const mae = meanAbsError(values, fitted);
    const mape = meanAbsPctError(values, fitted);
    const forecast = Array.from({ length: ahead }, (_, i) => Math.max(0, Math.round(intercept + slope * (n + i))));
    return { skuId, method: 'linear_trend', forecast, mae, mape, fitScore: mae };
}
function meanAbsError(actual, predicted) {
    if (actual.length === 0)
        return 0;
    return actual.reduce((sum, v, i) => sum + Math.abs(v - predicted[i]), 0) / actual.length;
}
function meanAbsPctError(actual, predicted) {
    const validPairs = actual.filter((v) => v !== 0);
    if (validPairs.length === 0)
        return 0;
    return actual.reduce((sum, v, i) => v !== 0 ? sum + Math.abs((v - predicted[i]) / v) : 0, 0) / validPairs.length * 100;
}
//# sourceMappingURL=timeSeriesForecaster.js.map